import React, { useState, useEffect } from "react";
import config from "../../config/config";

const DashboardHome = ({ onNavigate }) => {
  const [stats, setStats] = useState({
    totalPages: 0,
    totalNavigation: 0,
    totalImages: 0,
    totalContactSubmissions: 0,
    totalCareerSubmissions: 0,
    totalServices: 0,
    totalProducts: 0,
    recentContactSubmissions: [],
    recentCareerSubmissions: [],
    pendingCareerSubmissions: 0,
    activeServices: 0,
    activeProducts: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("get_dashboard_stats.php"), {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();

      if (data.success) {
        setStats(data.data);
      } else {
        setError("Failed to fetch dashboard stats: " + data.message);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setError("Error fetching dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="dashboard-home">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-home">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      {/* Welcome Header */}
      <div className="welcome-header">
        <div className="welcome-content">
          <h1>Welcome to NCG Admin Dashboard</h1>
          <p>
            Manage your website content, forms, and products from one central
            location
          </p>
        </div>
        <div className="welcome-icon">
          <i className="fas fa-tachometer-alt"></i>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon pages">
            <i className="fas fa-file-alt"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalPages}</h3>
            <p>Total Pages</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon navigation">
            <i className="fas fa-sitemap"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalNavigation}</h3>
            <p>Navigation Items</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon images">
            <i className="fas fa-images"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalImages}</h3>
            <p>Header Images</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon contact">
            <i className="fas fa-envelope"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalContactSubmissions}</h3>
            <p>Contact Submissions</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon career">
            <i className="fas fa-briefcase"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalCareerSubmissions}</h3>
            <p>Career Applications</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon pending">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.pendingCareerSubmissions}</h3>
            <p>Pending Applications</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon services">
            <i className="fas fa-cogs"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.activeServices}</h3>
            <p>Active Services</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon products">
            <i className="fas fa-box"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.activeProducts}</h3>
            <p>Active Products</p>
          </div>
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="recent-activity-section">
        <div className="row">
          {/* Recent Contact Submissions */}
          <div className="col-lg-6">
            <div className="activity-card">
              <div className="activity-header">
                <h3>
                  <i className="fas fa-envelope text-primary"></i>
                  Recent Contact Submissions
                </h3>
                <button
                  onClick={() => onNavigate("contact-data")}
                  className="view-all-link"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  View All
                </button>
              </div>
              <div className="activity-content">
                {stats.recentContactSubmissions.length > 0 ? (
                  <div className="activity-list">
                    {stats.recentContactSubmissions.map((submission) => (
                      <div key={submission.id} className="activity-item">
                        <div className="activity-icon">
                          <i className="fas fa-user"></i>
                        </div>
                        <div className="activity-details">
                          <div className="activity-title">
                            {submission.name}
                          </div>
                          <div className="activity-subtitle">
                            {submission.email}
                          </div>
                          <div className="activity-time">
                            {formatDate(submission.created_at)}
                          </div>
                        </div>
                        <div className="activity-status">
                          <span className="badge bg-success">New</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-activity">
                    <i className="fas fa-inbox"></i>
                    <p>No recent contact submissions</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Career Applications */}
          <div className="col-lg-6">
            <div className="activity-card">
              <div className="activity-header">
                <h3>
                  <i className="fas fa-briefcase text-warning"></i>
                  Recent Career Applications
                </h3>
                <button
                  onClick={() => onNavigate("career-data")}
                  className="view-all-link"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  View All
                </button>
              </div>
              <div className="activity-content">
                {stats.recentCareerSubmissions.length > 0 ? (
                  <div className="activity-list">
                    {stats.recentCareerSubmissions.map((application) => (
                      <div key={application.id} className="activity-item">
                        <div className="activity-icon">
                          <i className="fas fa-user-tie"></i>
                        </div>
                        <div className="activity-details">
                          <div className="activity-title">
                            {application.first_name} {application.last_name}
                          </div>
                          <div className="activity-subtitle">
                            {application.position} • {application.email}
                          </div>
                          <div className="activity-time">
                            {formatDate(application.created_at)}
                          </div>
                        </div>
                        <div className="activity-status">
                          <span
                            className={`badge ${
                              application.status === "pending"
                                ? "bg-warning"
                                : application.status === "reviewed"
                                ? "bg-info"
                                : application.status === "shortlisted"
                                ? "bg-primary"
                                : application.status === "hired"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                          >
                            {application.status.charAt(0).toUpperCase() +
                              application.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-activity">
                    <i className="fas fa-briefcase"></i>
                    <p>No recent career applications</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          <button
            onClick={() => onNavigate("pages")}
            className="quick-action-card"
          >
            <div className="quick-action-icon">
              <i className="fas fa-file-alt"></i>
            </div>
            <div className="quick-action-content">
              <h4>Manage Pages</h4>
              <p>Edit page content and settings</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate("services")}
            className="quick-action-card"
          >
            <div className="quick-action-icon">
              <i className="fas fa-cogs"></i>
            </div>
            <div className="quick-action-content">
              <h4>Manage Services</h4>
              <p>Add, edit, or remove services</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate("products")}
            className="quick-action-card"
          >
            <div className="quick-action-icon">
              <i className="fas fa-box"></i>
            </div>
            <div className="quick-action-content">
              <h4>Manage Products</h4>
              <p>Add, edit, or remove products</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate("contact-data")}
            className="quick-action-card"
          >
            <div className="quick-action-icon">
              <i className="fas fa-envelope"></i>
            </div>
            <div className="quick-action-content">
              <h4>View Contact Forms</h4>
              <p>Check recent contact submissions</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate("career-data")}
            className="quick-action-card"
          >
            <div className="quick-action-icon">
              <i className="fas fa-briefcase"></i>
            </div>
            <div className="quick-action-content">
              <h4>View Career Applications</h4>
              <p>Review job applications</p>
            </div>
          </button>

          <button
            onClick={() => onNavigate("images")}
            className="quick-action-card"
          >
            <div className="quick-action-icon">
              <i className="fas fa-images"></i>
            </div>
            <div className="quick-action-content">
              <h4>Manage Images</h4>
              <p>Upload and manage header images</p>
            </div>
          </button>
        </div>
      </div>

      <style jsx>{`
        .dashboard-home {
          padding: 2rem;
          background: #f8f9fa;
          min-height: 100vh;
        }

        .welcome-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem;
          border-radius: 16px;
          margin-bottom: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
        }

        .welcome-content h1 {
          margin: 0 0 0.5rem 0;
          font-size: 2.5rem;
          font-weight: 700;
        }

        .welcome-content p {
          margin: 0;
          font-size: 1.1rem;
          opacity: 0.9;
        }

        .welcome-icon {
          font-size: 4rem;
          opacity: 0.8;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          margin-right: 1rem;
        }

        .stat-icon.pages {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .stat-icon.navigation {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .stat-icon.images {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }

        .stat-icon.contact {
          background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
        }

        .stat-icon.career {
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
        }

        .stat-icon.pending {
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          color: #8b4513;
        }

        .stat-icon.services {
          background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
          color: #2c3e50;
        }

        .stat-icon.products {
          background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
          color: #8b4513;
        }

        .stat-content h3 {
          margin: 0;
          font-size: 2rem;
          font-weight: 700;
          color: #2c3e50;
        }

        .stat-content p {
          margin: 0.25rem 0 0 0;
          color: #6c757d;
          font-weight: 500;
        }

        .recent-activity-section {
          margin-bottom: 2rem;
        }

        .activity-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          height: 100%;
        }

        .activity-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .activity-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #2c3e50;
        }

        .activity-header h3 i {
          margin-right: 0.5rem;
        }

        .view-all-link {
          color: #007bff;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .view-all-link:hover {
          text-decoration: underline;
        }

        .activity-content {
          padding: 1rem;
        }

        .activity-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .activity-item {
          display: flex;
          align-items: center;
          padding: 1rem;
          border-bottom: 1px solid #f8f9fa;
          transition: background-color 0.2s ease;
        }

        .activity-item:last-child {
          border-bottom: none;
        }

        .activity-item:hover {
          background-color: #f8f9fa;
        }

        .activity-icon {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: #e3f2fd;
          color: #1976d2;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          font-size: 1rem;
        }

        .activity-details {
          flex: 1;
        }

        .activity-title {
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 0.25rem;
        }

        .activity-subtitle {
          color: #6c757d;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }

        .activity-time {
          color: #adb5bd;
          font-size: 0.8rem;
        }

        .activity-status {
          margin-left: 1rem;
        }

        .no-activity {
          text-align: center;
          padding: 2rem;
          color: #6c757d;
        }

        .no-activity i {
          font-size: 3rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .quick-actions-section {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .quick-actions-section h3 {
          margin: 0 0 1.5rem 0;
          color: #2c3e50;
          font-weight: 600;
        }

        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }

        .quick-action-card {
          display: flex;
          align-items: center;
          padding: 1.5rem;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease;
          background: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }

        .quick-action-card:hover {
          border-color: #007bff;
          background-color: #f8f9ff;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
        }

        .quick-action-icon {
          width: 50px;
          height: 50px;
          border-radius: 10px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          margin-right: 1rem;
        }

        .quick-action-content h4 {
          margin: 0 0 0.25rem 0;
          color: #2c3e50;
          font-weight: 600;
        }

        .quick-action-content p {
          margin: 0;
          color: #6c757d;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .dashboard-home {
            padding: 1rem;
          }

          .welcome-header {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
          }

          .welcome-content h1 {
            font-size: 2rem;
          }

          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          }

          .quick-actions-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardHome;
